import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedStudent } from "../../../state/reducers/appSlice";
import { getStudentDetails } from "../../../services/admin.services";

// Component imports
import ApplicationListTable from "./ApplicationListTable";
import Message_Notification from "./Message_Notification";
import ShortlistingTable from "./ShortlistingTable";
import StatusTrackingTable from "./StatusTrackingTable";
import FinalizedApplication from "./FinalizedApplication";

// Icons
import {
  Users,
  MessageSquare,
  CheckSquare,
  BarChart2,
  FileCheck,
  Archive,
} from "lucide-react";

const ApplicationList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "applications";

  const setActiveTab = (tabId) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };
  const location = useLocation();
  const dispatch = useDispatch();
  const allStudents = useSelector((state) => state.app.allStudents);

  const completedCount = useMemo(() => {
    return allStudents.filter((s) => {
      const status = (s?.status || "").toLowerCase();
      const archived = s?.loan_types?.__archived__ === true;
      return status === "complete" && !archived;
    }).length;
  }, [allStudents]);

  // Sample data counts
  const [applicationCount, setApplicationCount] = useState(30);
  const [messageCount, setMessageCount] = useState(10);
  const [shortlistingCount, setShortlistingCount] = useState(2);
  const [statusCount, setStatusCount] = useState(5);

  const fetchStudentDetails = async () => {
    try {
      const res = await getStudentDetails(location.pathname.split("/")[3]);
      dispatch(setSelectedStudent(res.data));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchStudentDetails();
    // Additional fetch logic for counts could go here
  }, [location.pathname]);

  // Tab configuration
  const tabs = [
    {
      id: "applications",
      label: "Student's List",
      icon: <Users size={20} />,
      count: applicationCount,
      showCount: false,
    },
    {
      id: "completed",
      label: "Completed Students",
      icon: <CheckSquare size={20} />,
      count: completedCount,
      showCount: true,
    },
    // {
    //   id: "shortlisting",
    //   label: "Shortlisting",
    //   icon: <CheckSquare size={20} />,
    //   count: shortlistingCount,
    //   showCount: false,
    // },
    {
      id: "status",
      label: "Status & Tracking",
      icon: <BarChart2 size={20} />,
      count: statusCount,
      showCount: false,
    },
    {
      id: "finalized-application",
      label: "Finalized Application",
      icon: <FileCheck size={20} />,
      count: shortlistingCount,
      showCount: false,
    },
    {
      id: "archived",
      label: "Archived Students",
      icon: <Archive size={20} />,
      count: 0,
      showCount: false,
    },
  ];

  // Commented out but kept for future reference
  // const messagesTab = {
  //   id: "messages",
  //   label: "Messages & Notifications",
  //   icon: <MessageSquare size={20} />,
  //   count: messageCount,
  //   showCount: true
  // };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4 sm:p-6">
      <div className="max-w-full mx-auto">
        {/* Page Header */}
        <div className="mb-6 rounded-2xl border border-indigo-100 bg-white/90 px-5 py-4 shadow-sm backdrop-blur sm:px-6">
          <h1 className="text-2xl font-bold text-slate-800">
            Application Management
          </h1>
          <p className="mt-1 text-sm text-slate-600 sm:text-base">
            Manage student applications, shortlisting, and status tracking
          </p>
        </div>

        {/* Tabs Navigation - Card Style */}
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 rounded-xl px-4 py-3 text-sm transition-all duration-200 focus:outline-none sm:px-5 ${
                  activeTab === tab.id
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <span
                  className={
                    activeTab === tab.id ? "text-white" : "text-slate-500"
                  }
                >
                  {tab.icon}
                </span>
                <span>{tab.label}</span>
                {tab.showCount && tab.count > 0 && (
                  <span
                    className={`ml-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      activeTab === tab.id
                        ? "bg-white/20 text-white"
                        : "bg-rose-100 text-rose-700"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          {activeTab === "applications" && <ApplicationListTable />}
          {activeTab === "messages" && <Message_Notification />}
          {activeTab === "shortlisting" && <ShortlistingTable />}
          {activeTab === "status" && <StatusTrackingTable />}
          {activeTab === "finalized-application" && <FinalizedApplication />}
          {activeTab === "completed" && (
            <ApplicationListTable showCompletedOnly={true} />
          )}
          {activeTab === "archived" && (
            <ApplicationListTable archived={true} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationList;
